/**
 * The chatbot's wording, from "PbDQ_Front-end_Chatbot & Sign-up_Flow_vFF"
 * (17 Sep 2026). Section ids match the document. Where a product-owner decision
 * changed a line, the change is noted beside it.
 */

export const LEGAL_TERMS_URL = '/terms-of-use';
export const LEGAL_PRIVACY_URL = 'https://www.poweredbydq.com/privacy-policy/';

/** DS-2 / DP-2 / DP-3 / DP-4 page directory — pages on the Powered by DQ website. */
export const WEBSITE_URL = 'https://www.poweredbydq.com';

/*
 * An agent message is laid out as the document lays it out (16 Oct 2026): one line
 * per entry, and the line the document sets in bold is `b(…)`. <Message> in
 * SignupFields renders it.
 */
const b = (text) => ({ bold: text });

export const TEXT = {
    CS1: {
        message: ['Hi, and welcome to Powered by DQ. Let’s start in the right place.', b('Which of these sounds like you?')],
        options: [
            { value: 'direct', label: 'For my organization', detail: 'We’ll use the solutions ourselves.' },
            { value: 'dq_pro', label: 'Become a DQ Pro', detail: 'We’ll deliver these solutions to our clients.' },
        ],
    },
    DS1: ['Good! Let’s find the right fit for your organization.', b('Which solution would you like to get started with?')],
    DP1: ['Good! Let’s build out what you’ll offer.', b('Which solution would you like to offer your clients?')],
    REVIEW: ['Good choice!', b('Explore the page to see what’s included in the solution and how it works.')],
    DIFFERENT_SOLUTION: 'Show me a different solution',
    DS3: [b('Tell us about your size of deployment.')],
    DS3_6: [b('Tell us about the program(s) you’d like to validate.')],
    // Asked in addition to the document (product-owner decision, 17 Sep 2026): a direct
    // buyer's login is a school or a school network.
    ACCOUNT_TYPE: {
        label: 'Is this for a single school or a school network?*',
        options: [
            { value: 'school', label: 'A single school' },
            { value: 'school_network', label: 'A school network' },
        ],
    },
    // DS-3.2 / 3.3 / 3.5 — included credits are learners ÷ 10, per the document's
    // worked examples (DS-3.2/3.3); the DS-5 order-review footnote says ÷5, but the
    // on-screen calculator with examples is treated as authoritative (product-owner
    // decision, 21 Sep 2026).
    INCLUDED_CREDITS: 'Your deployment includes 1 creation credit for every 10 learners. We’ll calculate this automatically based on your learner count.',
    ADDITIONAL_CREDITS: {
        label: 'Additional LearnCreator credits*',
        help: 'Do you need more creation capacity? Additional LearnCreator credits can be purchased at any time from your workspace.',
    },
    CAPABILITIES: { label: 'Additional capabilities*', help: 'Select all that apply.' },
    CREDIT_EXPLAINER: {
        title: 'What is a LearnCreator credit?',
        body: 'LearnCreator credits are what you spend to create a learning program. One credit is roughly one learning session, 30 to 45 minutes. Credits are pooled across your deployment, and if you run out, more can be purchased at any time from your workspace.',
    },
    FRAMEWORK_EXPLAINER: {
        title: 'Which FrameworkCreator tier fits us?',
        body: 'Basic tier maps your role families onto the IEEE 3527.1 DQ Framework, Standard tier adds builder access and a guided method for adapting it, and Full tier builds a framework of your own across the enterprise.',
    },
    DURATION_HELP: 'How many hours of learning content does this program contain? Your validation package includes up to 20 hours of program content. If the program is longer than 20 hours, the additional validation scope will be calculated automatically based on total program duration.',
    PARTICIPANTS_HELP: {
        essential: 'Impact measurement is not included in the Essential validation package. You can add impact measurement separately if needed.',
        standard: 'Impact measurement for up to 100 participants is included in the Standard validation package. If your program has more than 100 participants, the additional impact measurement fee will be calculated automatically.',
        comprehensive: 'Impact measurement for up to 1,000 participants is included in the Comprehensive validation package. If your program has more than 1,000 participants, the additional impact measurement fee will be calculated automatically.',
    },
    GOV_NOTICE: {
        title: 'Government verification required',
        paragraphs: [
            'The COS Government Assessment is made available in collaboration with the Global Cybersecurity Forum (GCF) and is intended exclusively for authorized government use. Access is therefore restricted to:',
        ],
        list: [
            'Government authorities responsible for child online protection; or',
            'Organizations formally designated by a government to perform child online protection responsibilities on its behalf.',
        ],
        after: 'To proceed, you will be asked for your organization’s details and documentation confirming your organization’s government status or official designation. We ask that you consider this eligibility requirement carefully before continuing.',
        evidenceTitle: 'Acceptable evidence may include:',
        evidence: [
            'An official government letter or authorization',
            'Documentation confirming your organization’s statutory or delegated mandate',
            'A government webpage or official record identifying your organization and its role',
            'Other documentation demonstrating formal government designation',
        ],
    },
    DS4: [b('Now, tell us about you and your organization.'), 'This sets up your Powered by DQ account.'],
    DP6: [b('Tell us about you and your organization.')],
    // DP-6.1 (verification non-required) — the document gives this path extra
    // reassurance and a closing transition line that DP-6.2 (gov verification) omits.
    DP6_1_NOTE: 'We’ll use this information to create your DQ Pro applicant record and guide you through the commercial-route screening. Creating an applicant record does not confirm DQ Pro eligibility or activate wholesale pricing, purchasing access or DQ Pro branding rights.',
    DP6_1_NEXT: 'In the next step, we’ll ask how your organization intends to use Powered by DQ and determine whether the direct organizational route or the DQ Pro route is appropriate.',
    WE2: [b('Now, tell us about you and your organization.')],
    WEBSITE_HELP: 'If your organization does not have a website, you may provide a public organization profile or professional profile.',
    LEGAL_NAME_HELP: 'Enter the organization’s name exactly as it appears in its official registration records.',
    CONFIRM_AUTHORIZED: 'I confirm that I am authorized to provide this information on behalf of the organization.*',
    CONFIRM_ACCURATE: 'I confirm that the information provided is accurate and may be used by DQ to create an applicant record, assess the appropriate commercial route and conduct organizational verification.*',
    DOCUMENTS: (firstName) => [b(`Thanks, ${firstName}. Now, upload your verification documents.`)],
    DOCUMENT_FILE_HELP: 'PDF, JPG or PNG, up to 10MB.',
    DOCUMENT_LINK_HELP: 'For a government webpage or online record.',
    DOCUMENT_NOTE: 'Anything we should know about these documents?',
    GOV_SUBMITTED: {
        title: 'Your submission has been received.',
        body: (firstName, org, email) => [
            `Thank you, ${firstName}. We’ve received your verification request for the COS Government Assessment for ${org}.`,
            `Our team will review your documents, in coordination with the Global Cybersecurity Forum (GCF) where appropriate, and contact you at ${email}.`,
            'Access to the COS Government Assessment remains unavailable until verification is complete.',
            'If you have questions about further steps, please contact the DQ team at contact@dqinstitute.org.',
        ],
    },
    DS5: [b('Here’s your order.'), 'Check the details before you continue to payment.'],
    DS5_8: [b('Here’s your order.'), 'Check the details before you send your request.'],
    DS5_8_NEXT: (email) => `#DQEveryChild is scoped for each national rollout, so it's quoted rather than purchased online. Our team will review your request and contact you at ${email}.`,
    // Shown under the order: items the document gives no price for are not in the total
    // (product-owner decision, 17 Sep 2026).
    QUOTED_SEPARATELY: 'Items marked “quoted separately” are not included in today’s total. Our team will contact you about them.',
    TERMS: 'I agree to the Powered by DQ Terms of Service.*',
    DS7: {
        title: (firstName) => `Welcome to Powered by DQ, ${firstName}!`,
        body: (solution, email) => [
            `Your payment was successful, and your ${solution} has been unlocked.`,
            `We’ve sent a workspace activation link to ${email}. Follow the link in the email to activate your account and access your workspace.`,
        ],
    },
    // DS-7 while DQ has not activated the workspace yet: payment never activates it
    // (19 Oct 2026) — DQ does, and the activation email follows.
    DS7_PENDING: {
        title: (firstName) => `Thank you, ${firstName}!`,
        body: (solution, email) => [
            `Your payment was successful. Your ${solution} order is now with our team for activation.`,
            `Once your workspace is activated, we’ll send a workspace activation link to ${email}.`,
        ],
    },
    DP3: [
        b('Now, let’s explore how you can run our solution.'),
        'Managing clients, assigning programs and allocating licenses all happen in one workspace, so you can resell or deliver without standing anything up yourself.',
        'Explore the page to see how you can run it.',
    ],
    DP4: [
        b('And this is how you can potentially earn by reselling or distributing our solution.'),
        'You buy at wholesale and set your own retail price. The difference is yours to keep.',
        'Explore the calculator on the page to see what that looks like at your volume.',
    ],
    DP5: (solution) => [b(`Would you like to proceed with the sign-up for ${solution}?`)],
    DP7: {
        message: [b('Let us confirm the appropriate commercial route for your organization.')],
        label: 'How does your organization intend to use this solution?*',
        help: 'DQ Pro is intended for organizations that sell, implement or support Powered by DQ solutions for legally independent external clients. Organizations purchasing primarily for themselves, their employees or affiliated entities should use the direct organizational route.',
    },
    DP7_1: {
        title: 'The direct organizational route is the right fit.',
        body: [
            'Your intended use does not meet the eligibility requirements for DQ Pro, which is designed for organizations serving legally independent external clients.',
            'You can still use Powered by DQ solutions through the direct organizational route for your own organization, employees or affiliated entities.',
            'If your commercial model changes and you begin serving independent external clients, you may submit a new DQ Pro eligibility checklist.',
        ],
    },
    DP8: {
        message: [b('Before we open your partnership, a few questions about your organization and clients.')],
        help: 'Completing this assessment does not grant DQ Pro status. DQ will verify your organization, proposed services and external-client activity before enabling wholesale purchasing.',
        q1: '1. How many external client organizations do you currently serve?*',
        q2: '2. Who do you currently or plan to serve?*',
        q3: '3. Which statement best describes your intended use of Powered by DQ?*',
        q4: '4. Are the intended client organizations legally independent from your organization?*',
        q5: '5. What responsibilities will your organization undertake?*',
    },
    DP9: {
        title: 'Your submission has been received.',
        body: (org, email) => [
            `Thank you. We’ve received your DQ Pro eligibility checklist submission for ${org}.`,
            `The DQ team will review your organization, intended external-client activity, proposed responsibilities and supporting evidence. We’ll send the outcome to ${email}.`,
            'Until the review is complete, DQ Pro status, wholesale pricing, purchasing access and branding rights remain unavailable.',
        ],
    },
    DP9_1_UPLOAD: {
        title: 'Upload your documents',
        body: [
            'Proof of organization registration: certificate of incorporation, business registration, charity registration or equivalent.',
            'Evidence of an external client relationship: contract or redacted invoice with any client organization, a letter of intent, a letter or email from a client confirming they intend to work with you, or a published case study.',
            'If you do not yet have a client, upload a one-page description of your planned offering instead: what you will offer, to whom, and by when.',
        ],
    },
    DP11: {
        title: (firstName) => `Welcome to Powered by DQ, ${firstName}!`,
        body: (solution, email) => [
            `Your payment was successful, and your ${solution} has been unlocked in your Partner account.`,
            `We’ve sent a workspace activation link to ${email}.`,
            'Follow the link in the email to activate your account and access your workspace.',
            'When your first client is ready, add their organization in your workspace and buy the licenses there.',
            'To deploy Powered by DQ within your own organization or an affiliated entity, use the direct organizational purchasing route.',
        ],
    },
    // DP-11 while DQ has not activated the workspace yet: a partner's payment never
    // activates it (19 Oct 2026) — DQ does, and the activation email follows.
    DP11_PENDING: {
        title: (firstName) => `Thank you, ${firstName}!`,
        body: (solution, email) => [
            `Your payment was successful. Your ${solution} partnership is now with our team for activation.`,
            `Once your workspace is activated, we’ll send a workspace activation link to ${email}.`,
            'Your first year of solution access starts on the day your workspace is activated.',
            'To deploy Powered by DQ within your own organization or an affiliated entity, use the direct organizational purchasing route.',
        ],
    },
    WE1: (solution) => [
        b(`Join the ${solution} waitlist.`),
        `${solution} is coming soon. I can add your organization to the waitlist and notify you when it becomes available. I’ll ask a few short questions about your organization. This should take about two minutes.`,
    ],
    WE3: {
        title: 'You’re on the waitlist.',
        body: (firstName, org, solution, email) => [
            `Thank you, ${firstName}. We have recorded ${org}’s interest in ${solution}.`,
            `We’ll notify you at ${email} when the solution becomes available. If your requirements match an early-access opportunity, the DQ team may contact you sooner.`,
        ],
    },
    WIRE_SUBMITTED: (email) => `Thank you. We’ll review your transfer and activate your workspace once it is approved. We’ll send the activation email to ${email}.`,
};

/**
 * The public status page (/pbdq/status). The document has no wording for this page;
 * these lines follow its tone and are for the product owner to confirm.
 */
export const STATUS_PAGE = {
    TITLE: 'Application status',
    HEADING: 'Check your application',
    INTRO: 'Enter the reference from your confirmation email and the email address you applied with.',
    MISSING: 'Enter your application reference and email address.',
    NOT_FOUND: 'We could not find an application with that reference and email address.',
    PAY: 'Continue to payment',
    UPLOAD: 'Upload documents',
    LOGIN: 'Log in',
    HELP: 'Questions about your application? Contact the DQ team at contact@dqinstitute.org.',
};

/**
 * What the status page says for an application: a short label and what happens next.
 * @param {{ type, status, wire_status, workspace_ready }} a  the lookup's application
 * @returns {{ label: string, lines: string[], login?: boolean }}
 */
export const statusMessage = (a) => {
    const proApplicant = a.type === 'dq_pro_applicant';
    switch (a.status) {
        case 'draft':
            return { label: 'Not submitted', lines: ['This application has not been submitted yet.'] };
        case 'submitted':
            if (proApplicant) return { label: 'Under review', lines: ['The DQ team is reviewing your DQ Pro eligibility checklist. We’ll send the outcome to the email address you applied with.', 'You can add documents to support your application below.'] };
            if (a.type === 'gov_verification') return { label: 'Under review', lines: ['Our team is reviewing your verification documents and will contact you by email. Access to the COS Government Assessment remains unavailable until verification is complete.'] };
            if (a.type === 'dqeverychild') return { label: 'Under review', lines: ['Our team is reviewing your #DQEveryChild request and will contact you by email with a formal proposal.'] };
            if (a.type === 'waitlist') return { label: 'On the waitlist', lines: ['We’ll notify you by email when the solution becomes available.'] };
            return { label: 'Under review', lines: ['Our team is reviewing your submission.'] };
        case 'awaiting_payment':
            if (a.wire_status === 'under_review') return { label: 'Transfer under review', lines: ['We’ve received your proof of payment and are checking the bank transfer. We’ll email you once it is approved.'] };
            if (a.wire_status === 'awaiting_proof') return { label: 'Awaiting proof of payment', lines: ['Your bank transfer is set up. Once you’ve made the payment, upload your proof of payment to continue.'] };
            if (proApplicant) return { label: 'Approved — awaiting payment', lines: ['Your organization is eligible for DQ Pro. Complete your payment to activate your partnership.'] };
            return { label: 'Awaiting payment', lines: ['Your order is ready. Complete your payment to unlock your solution.'] };
        case 'paid':
            return { label: 'Payment received', lines: ['Thank you — your payment has been received. We’re getting your solution ready and will contact you by email.'] };
        case 'fulfilled':
            return a.workspace_ready || a.type !== 'direct_order'
                ? { label: 'Active', lines: ['Your workspace is active. Use the activation link we emailed you, or log in if you have already activated it.'], login: true }
                : { label: 'Delivered', lines: ['Your solution has been delivered. Contact the DQ team if you need anything further.'] };
        case 'approved':
            return { label: 'Approved', lines: ['Your verification has been approved. The DQ team will contact you by email about access.'] };
        case 'rejected':
            return { label: 'Reviewed', lines: ['Your application has been reviewed, and we’ve emailed you the outcome and the next step.'] };
        case 'closed':
            return proApplicant
                ? { label: 'Closed', lines: ['The direct organizational route is the right fit for your intended use. You can start a direct order at any time.'] }
                : { label: 'Closed', lines: ['The DQ team has handled this request. Contact us if you need anything further.'] };
        default:
            return { label: 'In progress', lines: ['Your application is in progress.'] };
    }
};
